
import React, { useEffect, useState } from "react";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import {
  TextFilter,
  BooleanFilter,
  TableAbstract,
} from "shared/components/Components";
import { useMercuryContext } from "../../context";
import { descriptionColumn } from "shared/table/columnsConfig";
import PermissionWrapper from "shared/components/PermissionWrapper";
import CustomerApplicationsFilter from "filters/CustomerApplications.filter";
import { Tag, Tooltip } from "antd";
import TimezoneConverter from "components/timezone-converter";
import TableTitle from "components/table/TableTitle";
import GetActiveStatusIcon from "shared/components/GetActiveStatusIcon";
import "manage/api-clients/manage-api-clients.scss";
import ApiClientRequest from "./ApiClientRequest";
import { hideString } from "../../common/utils/helpers";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { ApiClient, ApiClientsProps, Application, Permission } from "./types/apiClients";
import { handleApiError } from "common/utils/handleApiError";

class ManageApiClients extends TableAbstract<ApiClient> {
  applicationsList: Application[] = [];
  permissionsList: Permission[] = [];
  visibleClientIds = new Set<string>();
  constructor(props: ApiClientsProps) {
    super(props);
    this.applicationsList = props.applicationsList || [];
    this.permissionsList = props.permissionsList || [];
    this.state = {
      ...this.state,
    };
  }

  defaultSortField = "name";

  FilterComponents = [
    { id: "name", name: "name", title: "Client Name", component: TextFilter, active: true },
    {
      id: "customerApplication",
      name: "customerApplication",
      title: "Customer Application",
      component: (props: any) => (
        <CustomerApplicationsFilter {...props} data={(this.props as ApiClientsProps)?.applicationsList} />
      ),
      active: true
    },
    { id: "isActive", name: "isActive", title: "Active Status", component: BooleanFilter, active: true },
  ];

  renderTags(text: string | string[]) {
    if (typeof text === "string") {
      return (
        <Tag color="var(--tertiary-color)" style={{ margin: "0 2px 2px 0", padding: "0 4px" }}>
          {text}
        </Tag>
      );
    }
    return text?.map((item, index) => (
      <Tag key={index} color="var(--tertiary-color)" style={{ margin: "0 2px 2px 0", padding: "0 4px" }}>
        {item}
      </Tag>
    ));
  }


  toggleClientIdVisibility = (row: ApiClient) => {
    const idKey = row.id || row.key;
    if (this.visibleClientIds.has(idKey)) {
      this.visibleClientIds.delete(idKey);
    } else {
      this.visibleClientIds.add(idKey);
    }
    this.forceUpdate();
  };


  columns = [
    { id: "name", title: "Client Name", dataIndex: "name", sorter: true },
    descriptionColumn,
    {
      id: "customerApplication",
      title: "Customer Application",
      dataIndex: "customerApplicationName",
      render: (record: any) => record ? this.renderTags(record) : ""
    },
    {
      id: "clientId",
      title: "Client ID",
      dataIndex: "clientId",
      width: 200,
      render: (value: string, row: ApiClient) => {
        if (!value) return "N/A";
        const idKey = row.id || row.key;
        const isVisible = this.visibleClientIds.has(idKey);
        return (
          <div className="client-id-container" style={{ display: 'flex', alignItems: 'center' }}>

            <span style={{ minWidth: 0, flex: '1 1 auto', wordBreak: 'break-all', overflowWrap: 'anywhere' }}>
              {isVisible ? value : hideString(value)}
            </span>

            {isVisible ? (
              <EyeInvisibleOutlined className="view-icon" onClick={() => this.toggleClientIdVisibility(row)} style={{color: 'var(--primary-color)', flex: '0 0 auto', whiteSpace: 'nowrap' }} />
            ) : (
              <Tooltip title={`View ID for client: ${row.name}`} placement="right">
                <EyeOutlined className="view-icon" onClick={() => this.toggleClientIdVisibility(row)} style={{color: 'var(--primary-color)' }} />
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      id: "isActive",
      title: <TableTitle title="Active" /> as any,
      dataIndex: "isActive",
      sorter: true,
      width: 80,
      align: "center" as any,
      render: (value: boolean | null | undefined) => <GetActiveStatusIcon status={value} />
    },
    {
      id: "createdAt",
      title: "Created Date",
      dataIndex: "createdAt",
      sorter: true,
      width: 150,
      render: (text: string) => <TimezoneConverter date={text} />
    },
    {
      id: "updatedAt",
      title: "Last Update Date",
      dataIndex: "updatedAt",
      sorter: true,
      width: 150,
      render: (text: string) => <TimezoneConverter date={text} />
    }
  ];

  renderActions() {
    const userPermissions = this.userContext.userDetails?.permissions || [];
    // const hasCreateAccess = checkPermissions(userPermissions, [
    //   { customerApplication: "Google", permissionName: "Manage Api Clients", action: "create" },
    // ]);
    const hasCreateAccess = true;
    // const hasEditAccess = checkPermissions(userPermissions, [
    //   { customerApplication: "Google", permissionName: "Manage Api Clients", action: "edit" },
    // ]);
    const hasEditAccess = true;
    const selectedClient = this.state?.selectedRows[0];
    return (
      <>
        <PermissionWrapper
          tooltip={!hasCreateAccess ? "No permission to add a new API client." : ""}
          hasPermissions={hasCreateAccess}
          children={
            <ApiClientRequest
              applicationsList={(this.props as ApiClientsProps).applicationsList}
              permissionsList={(this.props as ApiClientsProps).permissionsList}
              buttonLabel="Add API Client"
              disabled={!hasCreateAccess}
              hasPermissions={hasCreateAccess}
              refresh={() => this.reload()}
              mode="add"
            />
          }
        />
        <span style={{ display: "inline-block", width: 12 }} />
        <PermissionWrapper
          tooltip={
            !hasEditAccess
              ? "No permission to edit a Api client."
              : this.state.selectedRows.length !== 1
                ? "Select 1 Api client to enable edit option"
                : ""
          }
          hasPermissions={hasEditAccess && this.state.selectedRows.length === 1}
          children={
            <ApiClientRequest
              initialValues={selectedClient}
              applicationsList={(this.props as ApiClientsProps).applicationsList}
              permissionsList={(this.props as ApiClientsProps).permissionsList}
              mode="edit"
              disabled={!hasEditAccess || this.state.selectedRows.length !== 1}
              hasPermissions={hasEditAccess && this.state.selectedRows.length === 1}
              refresh={() => this.reload()}
            />
          }
        />
      </>
    );
  }

  rowClassName = (_record: ApiClient): string => "row";

  async fetch(): Promise<any> {
    const { sortOrder, sortField, currentPage, itemsPerPage } = this.state;

    const payload = {
      filter: this.filtersToQuery(),
      pagination: {
        pageNumber: currentPage || 1,
        resultsPerPage: itemsPerPage
      },
      sort: {
        [sortField || "createdAt"]: sortOrder === "ascend" ? 1 : -1
      }
    };

    const response = await this.userContext.umsClient.post(`/ums-v1/api-clients/search`, payload);
    const documents: ApiClient[] = response?.data?.documents || [];

    const meta = response?.data?.meta ?? {
      total: 0,
      page: currentPage,
      pages: 1,
      skip: 0,
      limit: itemsPerPage,
      match: {},
      sort: { createdAt: -1 }
    };

    return { data: { documents: documents, meta } };
  }

  pageTitle = "Manage Api Clients - Mercury © RWS";
  title = "Manage Api Clients";
  pageClass = "apiClientsTable";
}

const ManageApiClientsTable = () => {
  const context = useMercuryContext();
  const breaks = useBreakpoint();
  const [applicationsList, setApplicationsList] = useState<Application[]>([]);
  const [permissionsList, setPermissionsList] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [{ data: apps }, { data: permissions }] = await Promise.all([
          context.coreClient.get("core-v1/customerApplications/paginate?page=1&limit=500"),
          context.umsClient.get("/ums-v1/permissions")
        ]);
        setApplicationsList(apps?.[0]?.documents || []);
        setPermissionsList(permissions || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [context]);

  if (loading || !applicationsList.length || !permissionsList.length) return null;

  return (
    <ManageApiClients
      {...({
        id: "ManageApiClients",
        context,
        breaks,
        useLegacyEndpoints: false,
        hasSidebar: true,
        applicationsList,
        permissionsList
      } as ApiClientsProps)}
      useEstimatedRowHeight
    />
  );
};

export default ManageApiClientsTable;
